import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { ChevronRight, ChevronLeft, Hash, Package, MapPin, FileText, Check, Plus, Minus, ChevronDown, ChevronUp, AlertCircle, CreditCard, Download, ExternalLink, Info, Mail, Truck, Loader2, Printer } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { generatePackingSlipPDF, openPrintableVersion } from '../utils/pdfGenerator';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_nice-grading-app/artifacts/iks4rsh0_Nice%20logo%200925%20%283%29.jpg';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewSubmissionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeSubmissionId = searchParams.get('resume');
  const { user, isAuthenticated, isLoading: authLoading, checkAuth } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Submission state (from database)
  const [submission, setSubmission] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  
  // Step 1: Quantity
  const [cardQuantity, setCardQuantity] = useState(1);
  const [pricingRules, setPricingRules] = useState([]);
  const [dealerPricingInfo, setDealerPricingInfo] = useState(null);
  const [isLoadingDealerPricing, setIsLoadingDealerPricing] = useState(false);
  const [promoPricing, setPromoPricing] = useState(null);  // User's promo pricing info
  
  // Voucher state
  const [voucherCode, setVoucherCode] = useState('');
  const [appliedVouchers, setAppliedVouchers] = useState([]);
  const [voucherError, setVoucherError] = useState(null);
  const [isValidatingVoucher, setIsValidatingVoucher] = useState(false);
  const [showVoucherInput, setShowVoucherInput] = useState(false);

  // Step 2: Card details
  const [cards, setCards] = useState([]);
  const [expandedCards, setExpandedCards] = useState({});

  // Step 3: Address state
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [estimatedValue, setEstimatedValue] = useState('');
  const [estimatedValueDisplay, setEstimatedValueDisplay] = useState('');  // Formatted display value
  
  const [newAddress, setNewAddress] = useState({
    label: 'Home',
    name: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    is_default: false
  });
  const [saveAddress, setSaveAddress] = useState(true);

  // Step 4: Review data and payment
  const [reviewData, setReviewData] = useState(null);
  const [savedPaymentMethod, setSavedPaymentMethod] = useState(null);
  const [paymentChoice, setPaymentChoice] = useState('saved'); // 'saved' or 'new'
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [showCreatingSubmission, setShowCreatingSubmission] = useState(false); // Transition state

  // Progress bar steps (4 logical steps)
  const progressSteps = [
    { number: 1, title: 'Add Cards', icon: Hash },
    { number: 2, title: 'Shipping Info', icon: MapPin },
    { number: 3, title: 'Review Order', icon: CreditCard },
    { number: 4, title: 'Confirmation', icon: Check }
  ];

  // Map internal step (1-5) to progress bar step (1-4)
  const getProgressStep = (internalStep) => {
    if (internalStep <= 2) return 1; // Quantity + Card Details = Add Cards
    if (internalStep === 3) return 2; // Return Address = Shipping Info
    if (internalStep === 4) return 3; // Review = Review Order
    return 4; // Confirmation
  };

  // ==================== ESTIMATED VALUE HELPERS ====================
  
  // Sanitize input: remove $, commas, spaces - keep only numbers and decimal point
  const sanitizeEstimatedValue = (input) => {
    if (!input) return '';
    // Remove everything except digits and decimal point
    const sanitized = String(input).replace(/[^0-9.]/g, '');
    // Ensure only one decimal point
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    return sanitized;
  };

  // Format number with commas for display
  const formatWithCommas = (value) => {
    if (!value) return '';
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    // Format with commas, no decimal if whole number
    if (Number.isInteger(num)) {
      return num.toLocaleString('en-US');
    }
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Handle estimated value input change
  const handleEstimatedValueChange = (e) => {
    const rawInput = e.target.value;
    // Sanitize the input
    const cleanValue = sanitizeEstimatedValue(rawInput);
    // Store the clean numeric value
    setEstimatedValue(cleanValue);
    // Format for display (only if we have a valid number)
    if (cleanValue && !isNaN(parseFloat(cleanValue))) {
      setEstimatedValueDisplay(formatWithCommas(cleanValue));
    } else {
      setEstimatedValueDisplay(rawInput); // Keep raw input if not a valid number yet
    }
  };

  // Handle blur - ensure proper formatting
  const handleEstimatedValueBlur = () => {
    if (estimatedValue && !isNaN(parseFloat(estimatedValue))) {
      setEstimatedValueDisplay(formatWithCommas(estimatedValue));
    }
  };

  // Handle focus - show raw number for easier editing
  const handleEstimatedValueFocus = () => {
    if (estimatedValue) {
      setEstimatedValueDisplay(estimatedValue);
    }
  };

  // Get parsed numeric value
  const getEstimatedValueNumber = () => {
    const num = parseFloat(estimatedValue);
    return isNaN(num) ? 0 : num;
  };

  // Handle PDF packing slip download
  const handleDownloadPackingSlip = async () => {
    if (!submission || !submissionId) {
      setPdfError('Submission data not available. Please refresh the page and try again.');
      return;
    }

    setIsGeneratingPDF(true);
    setPdfError(null);
    
    try {
      // Fetch full submission data including cards and shipping address
      // API returns SubmissionWithCards model - submission data is at root level
      const response = await axios.get(`${API}/submissions/${submissionId}/review`);
      const fullData = response.data;
      
      // Get shipping address from saved addresses
      const selectedAddress = savedAddresses.find(a => a.id === selectedAddressId) || fullData.shipping_address;
      
      // Generate the PDF - fullData IS the submission (not fullData.submission)
      await generatePackingSlipPDF(
        fullData,
        user,
        fullData.cards || cards,
        selectedAddress
      );
    } catch (err) {
      console.error('Failed to generate packing slip:', err);
      setPdfError('Unable to generate PDF. Please try the printable version below or contact support.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Handle printable version (fallback)
  const handlePrintableVersion = async () => {
    if (!submission || !submissionId) {
      setPdfError('Submission data not available. Please refresh the page and try again.');
      return;
    }

    try {
      // API returns SubmissionWithCards model - submission data is at root level
      const response = await axios.get(`${API}/submissions/${submissionId}/review`);
      const fullData = response.data;
      const selectedAddress = savedAddresses.find(a => a.id === selectedAddressId) || fullData.shipping_address;
      
      // fullData IS the submission (not fullData.submission)
      openPrintableVersion(
        fullData,
        user,
        fullData.cards || cards,
        selectedAddress
      );
    } catch (err) {
      console.error('Failed to open printable version:', err);
      setPdfError('Unable to open printable version. Please contact support.');
    }
  };

  const currentProgressStep = getProgressStep(currentStep);

  // Fetch pricing rules on mount
  useEffect(() => {
    fetchPricingRules();
    fetchPromoPricing();
  }, []);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/submit' } } });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Handle resume parameter - load draft submission and go to review step
  useEffect(() => {
    const loadResumeSubmission = async () => {
      if (!resumeSubmissionId || !isAuthenticated || authLoading) return;
      
      try {
        setIsLoading(true);
        
        // Set the submission ID first
        setSubmissionId(resumeSubmissionId);
        
        // Fetch review data
        const response = await axios.get(`${API}/submissions/${resumeSubmissionId}/review`);
        const data = response.data;
        
        // Set reviewData for Step 4
        setReviewData(data);
        
        // Populate Step 1 state (Quantity)
        const cardCount = data.card_count || 1;
        setCardQuantity(cardCount);
        
        // Populate Step 2 state (Cards)
        // Load saved cards and add empty slots if needed to match card_count
        let loadedCards = data.cards || [];
        if (loadedCards.length < cardCount) {
          // Add empty card slots to match card_count
          const additionalCards = Array.from({ length: cardCount - loadedCards.length }, (_, index) => ({
            id: Date.now() + index,
            position_number: loadedCards.length + index + 1,
            year: '',
            set_brand: '',
            card_number: '',
            player_name: '',
            insert_parallel: ''
          }));
          loadedCards = [...loadedCards, ...additionalCards];
        }
        setCards(loadedCards);
        
        // Initialize expandedCards - expand the first card by default
        if (loadedCards.length > 0) {
          const expanded = {};
          expanded[loadedCards[0].id] = true;
          setExpandedCards(expanded);
        }
        
        // Populate Step 3 state (Shipping)
        if (data.shipping_address_id) {
          setSelectedAddressId(data.shipping_address_id);
        }
        // Note: API returns estimated_submission_value, not estimated_value
        if (data.estimated_submission_value) {
          const valueStr = data.estimated_submission_value.toString();
          setEstimatedValue(valueStr);
          // Format for display
          const num = parseFloat(valueStr);
          if (!isNaN(num)) {
            if (Number.isInteger(num)) {
              setEstimatedValueDisplay(num.toLocaleString('en-US'));
            } else {
              setEstimatedValueDisplay(num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
            }
          }
        }
        
        // Fetch addresses for Step 3
        await fetchAddresses();
        
        // Fetch saved payment method for Step 4
        await fetchSavedPaymentMethod();
        
        // Go to step 4 (review/payment)
        setCurrentStep(4);
      } catch (err) {
        console.error('Failed to load submission:', err);
        setError('Failed to load submission. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadResumeSubmission();
  }, [resumeSubmissionId, isAuthenticated, authLoading]);

  const fetchPricingRules = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setPricingRules(response.data);
    } catch (err) {
      console.error('Failed to fetch pricing rules:', err);
    }
  };

  const fetchPromoPricing = async () => {
    try {
      const response = await axios.get(`${API}/user/promo-pricing`);
      if (response.data.has_promo && response.data.is_valid) {
        setPromoPricing(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch promo pricing:', err);
    }
  };

  const fetchDealerPricing = async (cardCount) => {
    if (!user || user.dealer_status !== 'approved') {
      setDealerPricingInfo(null);
      return;
    }
    
    setIsLoadingDealerPricing(true);
    try {
      const response = await axios.get(`${API}/pricing/compare`, {
        params: { card_count: cardCount }
      });
      setDealerPricingInfo(response.data);
    } catch (err) {
      console.error('Failed to fetch dealer pricing:', err);
      setDealerPricingInfo(null);
    } finally {
      setIsLoadingDealerPricing(false);
    }
  };

  // Fetch dealer pricing when card quantity changes (debounced)
  useEffect(() => {
    if (user?.dealer_status === 'approved' && cardQuantity >= 1) {
      const timer = setTimeout(() => {
        fetchDealerPricing(cardQuantity);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [cardQuantity, user?.dealer_status]);

  const fetchAddresses = async () => {
    setIsLoadingAddresses(true);
    try {
      const response = await axios.get(`${API}/addresses`);
      setSavedAddresses(response.data);
      // Select default address if exists
      if (response.data.length > 0) {
        const defaultAddr = response.data.find(a => a.is_default) || response.data[0];
        setSelectedAddressId(defaultAddr.id);
      } else {
        setShowNewAddressForm(true);
      }
    } catch (err) {
      console.error('Failed to fetch addresses:', err);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  const fetchReviewData = async (subId) => {
    try {
      const response = await axios.get(`${API}/submissions/${subId}/review`);
      setReviewData(response.data);
      // Also fetch saved payment method
      await fetchSavedPaymentMethod();
    } catch (err) {
      console.error('Failed to fetch review data:', err);
    }
  };

  const fetchSavedPaymentMethod = async () => {
    try {
      const response = await axios.get(`${API}/payments/saved-method`);
      setSavedPaymentMethod(response.data);
      // If no saved method, default to 'new'
      if (!response.data.has_saved_method) {
        setPaymentChoice('new');
      }
    } catch (err) {
      console.error('Failed to fetch saved payment method:', err);
      setPaymentChoice('new');
    }
  };

  // Handle payment - either use saved method or redirect to Stripe
  const handlePayment = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (paymentChoice === 'saved' && savedPaymentMethod?.has_saved_method) {
        // Use saved payment method - show transition
        setShowCreatingSubmission(true);
        
        const response = await axios.post(`${API}/payments/use-saved-method`, {
          submission_id: submissionId
        });
        
        if (response.data.success) {
          // Send confirmation email via backend API
          await sendConfirmationEmailAPI();
          
          // Wait a moment to show the transition message (at least 2 seconds total)
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          setShowCreatingSubmission(false);
          setCurrentStep(5);
        }
      } else {
        // Redirect to Stripe to add/update payment method (no "creating submission" message)
        const response = await axios.post(`${API}/payments/setup-checkout`, {
          submission_id: submissionId,
          origin_url: window.location.origin
        });
        
        // Redirect to Stripe Checkout immediately
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Failed to process payment');
      setShowCreatingSubmission(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Send confirmation email via backend API
  const sendConfirmationEmailAPI = async () => {
    try {
      const response = await axios.post(`${API}/email/send-confirmation`, {
        submission_id: submissionId
      });
      console.log('📧 Confirmation email result:', response.data);
    } catch (err) {
      console.error('Failed to send confirmation email:', err);
      // Don't block the flow if email fails
    }
  };

  // Legacy console log for email (kept for reference)
  const sendConfirmationEmailConsole = () => {
    const emailContent = {
      to: user?.email,
      subject: `Submission Confirmed - ${submission?.submission_number}`,
      body: `
        Dear ${user?.name || 'Customer'},

        Thank you for your submission to NICE Grading!

        SUBMISSION DETAILS:
        -------------------
        Submission Number: ${submission?.submission_number}
        Cards: ${submission?.card_count} @ $${submission?.price_per_card?.toFixed(2)}/card
        Grading Total: $${submission?.total_price?.toFixed(2)}
        Return Shipping: $15.00
        Total: $${(submission?.total_price + 15).toFixed(2)}

        NEXT STEPS:
        -----------
        1. Download and print your packing slip from your dashboard
        2. Package your cards securely using card savers or top loaders
        3. Include the packing slip inside your package
        4. Ship your cards to the address matching your carrier:
           
           FOR USPS:
           NICE Grading
           Attn: Receiving
           P.O. Box 30041
           Rochester, NY 14603
           
           FOR UPS/FedEx:
           NICE Grading
           Attn: Receiving
           250 Cumberland St., Ste. 202
           Rochester, NY 14605
           
           Reference: Submission #${submission?.submission_number}

        5. We'll email you when your cards arrive
        6. Payment will be processed when we receive your cards

        IMPORTANT REMINDERS:
        -------------------
        • Package your cards securely to prevent damage
        • Include your packing slip inside the package
        • We recommend insuring your shipment
        • Keep your tracking number for your records

        Track your submission status at: ${window.location.origin}/dashboard

        Thank you for choosing NICE Grading!

        Best regards,
        The NICE Grading Team
      `
    };

    // Log email content (will be replaced with actual email service)
    console.log('📧 CONFIRMATION EMAIL:', emailContent);
    console.log('Email would be sent to:', emailContent.to);
    console.log('Subject:', emailContent.subject);
    console.log('Body:', emailContent.body);
  };

  const calculatePrice = () => {
    const cardCount = cardQuantity;
    
    // Find matching pricing rule
    const matchingRule = pricingRules
      .filter(r => r.is_active)
      .find(r => {
        if (cardCount >= r.min_cards) {
          if (r.max_cards === null || cardCount <= r.max_cards) {
            return true;
          }
        }
        return false;
      });
    
    if (matchingRule) {
      return {
        cardCount,
        pricePerCard: matchingRule.price_per_card,
        total: cardCount * matchingRule.price_per_card,
        tierName: matchingRule.tier_name,
        isLaunchSpecial: matchingRule.is_launch_special
      };
    }
    
    // Fallback pricing
    let pricePerCard = 16;
    if (cardCount >= 100) pricePerCard = 10;
    else if (cardCount >= 25) pricePerCard = 12;
    
    return { cardCount, pricePerCard, total: cardCount * pricePerCard };
  };

  const generateCards = () => {
    const newCards = Array.from({ length: cardQuantity }, (_, index) => ({
      id: Date.now() + index,
      position_number: index + 1,
      year: '',
      set_brand: '',
      card_number: '',
      player_name: '',
      insert_parallel: ''
    }));
    setCards(newCards);
    // Only expand the first card by default
    const initialExpanded = {};
    if (newCards.length > 0) {
      initialExpanded[newCards[0].id] = true;
    }
    setExpandedCards(initialExpanded);
  };

  const toggleCardExpand = (cardId) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  const updateCard = (id, field, value) => {
    setCards(cards.map(card => 
      card.id === id ? { ...card, [field]: value } : card
    ));
  };

  const handleNewAddressChange = (e) => {
    setNewAddress({
      ...newAddress,
      [e.target.name]: e.target.value
    });
  };

  const incrementQuantity = () => setCardQuantity(prev => prev + 1);
  const decrementQuantity = () => {
    if (cardQuantity > 1) setCardQuantity(prev => prev - 1);
  };

  // STEP 1: Create or update submission
  const handleStep1Complete = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (submissionId) {
        // RESUMING: Update existing submission quantity
        await axios.patch(`${API}/submissions/${submissionId}/step1`, {
          card_count: cardQuantity
        });
        
        // Only regenerate cards if quantity changed and we need more/fewer cards
        if (cards.length !== cardQuantity) {
          // Adjust cards array to match new quantity
          if (cardQuantity > cards.length) {
            // Add more empty cards
            const additionalCards = Array.from({ length: cardQuantity - cards.length }, (_, index) => ({
              id: Date.now() + index,
              position_number: cards.length + index + 1,
              year: '',
              set_brand: '',
              card_number: '',
              player_name: '',
              insert_parallel: ''
            }));
            setCards([...cards, ...additionalCards]);
          } else {
            // Trim cards to new quantity
            setCards(cards.slice(0, cardQuantity));
          }
        }
        setCurrentStep(2);
      } else {
        // NEW: Create new submission
        const response = await axios.post(`${API}/submissions/step1`, {
          card_count: cardQuantity,
          voucher_codes: appliedVouchers.map(v => v.code)
        });
        
        setSubmission(response.data);
        setSubmissionId(response.data.id);
        generateCards();
        setCurrentStep(2);
      }
    } catch (err) {
      console.error('Failed to process submission:', err);
      setError(err.response?.data?.detail || 'Failed to process submission');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Voucher handling
  const handleApplyVoucher = async () => {
    if (!voucherCode.trim()) return;
    
    // Check if already applied
    if (appliedVouchers.find(v => v.code.toUpperCase() === voucherCode.trim().toUpperCase())) {
      setVoucherError('This code is already applied');
      return;
    }
    
    setIsValidatingVoucher(true);
    setVoucherError(null);
    
    try {
      const response = await axios.post(
        `${API}/vouchers/validate?code=${voucherCode.trim()}&card_count=${cardQuantity}`
      );
      
      if (response.data.valid) {
        // Check stackability rules
        const hasNonStackable = appliedVouchers.some(v => !v.stackable);
        if (hasNonStackable && appliedVouchers.length > 0) {
          setVoucherError('Cannot add more codes - existing code is not stackable');
          return;
        }
        if (!response.data.stackable && appliedVouchers.length > 0) {
          setVoucherError('This code cannot be combined with other codes');
          return;
        }
        
        setAppliedVouchers([...appliedVouchers, response.data]);
        setVoucherCode('');
      } else {
        setVoucherError(response.data.error_message || 'Invalid code');
      }
    } catch (err) {
      setVoucherError(err.response?.data?.detail || 'Failed to validate code');
    } finally {
      setIsValidatingVoucher(false);
    }
  };
  
  const handleRemoveVoucher = (codeToRemove) => {
    setAppliedVouchers(appliedVouchers.filter(v => v.code !== codeToRemove));
  };
  
  const totalVoucherDiscount = appliedVouchers.reduce((sum, v) => sum + v.discount_amount, 0);

  // STEP 2: Save cards (optional)
  const handleStep2Complete = async (skip = false) => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (!skip) {
        // Filter out empty cards and save
        const cardsToSave = cards
          .filter(c => c.year || c.set_brand || c.card_number || c.player_name || c.insert_parallel)
          .map((c, index) => ({
            position_number: index + 1,
            year: c.year || null,
            set_brand: c.set_brand || null,
            card_number: c.card_number || null,
            player_name: c.player_name || null,
            insert_parallel: c.insert_parallel || null
          }));
        
        if (cardsToSave.length > 0) {
          await axios.post(`${API}/submissions/${submissionId}/cards`, cardsToSave);
        }
      }
      
      // Fetch addresses for step 3
      await fetchAddresses();
      setCurrentStep(3);
    } catch (err) {
      console.error('Failed to save cards:', err);
      setError(err.response?.data?.detail || 'Failed to save card details');
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 3: Save shipping address
  const handleStep3Complete = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      let addressId = selectedAddressId;
      
      // Create new address if needed
      if (showNewAddressForm) {
        if (!newAddress.name || !newAddress.street_address || !newAddress.city || !newAddress.state || !newAddress.zip_code) {
          setError('Please fill in all required address fields');
          setIsLoading(false);
          return;
        }
        
        if (saveAddress) {
          const addressResponse = await axios.post(`${API}/addresses`, newAddress);
          addressId = addressResponse.data.id;
        } else {
          // Still need to save address for the submission
          const addressResponse = await axios.post(`${API}/addresses`, {
            ...newAddress,
            label: 'Submission Address'
          });
          addressId = addressResponse.data.id;
        }
      }
      
      if (!addressId) {
        setError('Please select or add a shipping address');
        setIsLoading(false);
        return;
      }
      
      // Validate estimated value is provided
      const parsedEstimatedValue = parseFloat(sanitizeEstimatedValue(estimatedValue));
      if (!estimatedValue || isNaN(parsedEstimatedValue) || parsedEstimatedValue <= 0) {
        setError('Please enter the estimated value of your submission (must be greater than $0)');
        setIsLoading(false);
        return;
      }
      
      // Update submission with address and estimated value
      await axios.patch(`${API}/submissions/${submissionId}/step3`, {
        shipping_address_id: addressId,
        estimated_submission_value: parsedEstimatedValue
      });
      
      // Fetch review data
      await fetchReviewData(submissionId);
      setCurrentStep(4);
    } catch (err) {
      console.error('Failed to save address:', err);
      setError(err.response?.data?.detail || 'Failed to save shipping address');
    } finally {
      setIsLoading(false);
    }
  };

  // Navigate back
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const pricing = calculatePrice();

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-[#003594] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Creating Submission Transition Overlay */}
      {showCreatingSubmission && (
        <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 bg-[#003594]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-[#003594] animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Creating Your Submission</h2>
            <p className="text-gray-500">Please wait while we finalize your order...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center">
            <img src={LOGO_URL} alt="NICE Grading" className="h-10 w-auto" />
          </Link>
          <Link to="/dashboard" className="text-sm text-gray-500 hover:text-[#003594]">
            {currentStep === 5 ? 'Go to My Dashboard' : 'Cancel'}
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Steps */}
        <div className="mb-10">
          <div className="flex items-center justify-between">
            {progressSteps.map((step, index) => {
              const StepIcon = step.icon;
              // Check if this progress step is completed or current based on mapped step
              const isCompleted = currentProgressStep > step.number;
              const isCurrent = currentProgressStep === step.number;
              
              return (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      isCompleted ? 'bg-green-500 text-white' :
                      isCurrent ? 'bg-[#003594] text-white' :
                      'bg-gray-200 text-gray-400'
                    }`}>
                      {isCompleted ? <Check className="w-6 h-6" /> : <StepIcon className="w-6 h-6" />}
                    </div>
                    <span className={`mt-2 text-xs font-medium hidden sm:block ${
                      isCompleted ? 'text-green-600' :
                      isCurrent ? 'text-[#003594]' : 'text-gray-400'
                    }`}>
                      {step.title}
                    </span>
                  </div>
                  {index < progressSteps.length - 1 && (
                    <div className={`flex-1 h-1 mx-2 sm:mx-4 rounded ${
                      currentProgressStep > step.number ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Submission Number Display */}
        {submission && currentStep > 1 && currentStep < 5 && (
          <div className="mb-4 text-center">
            <span className="text-sm text-gray-500">Submission: </span>
            <span className="text-sm font-semibold text-[#003594]">{submission.submission_number}</span>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          
          {/* STEP 1: Quantity */}
          {currentStep === 1 && (
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">How Many Cards Are You Submitting?</h2>
              <p className="text-gray-500 mb-10">We&apos;ll use this to calculate the best pricing tier for you.</p>

              <div className="max-w-md mx-auto">
                <div className="flex items-center justify-center gap-4 mb-8">
                  <button
                    onClick={decrementQuantity}
                    disabled={cardQuantity <= 1}
                    className="w-12 h-12 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                  >
                    <Minus className="w-5 h-5 text-gray-600" />
                  </button>
                  
                  <Input
                    type="number"
                    min="1"
                    value={cardQuantity}
                    onChange={(e) => setCardQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-24 h-12 text-center text-2xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003594] focus:border-transparent"
                  />
                  
                  <button
                    onClick={incrementQuantity}
                    className="w-12 h-12 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 flex items-center justify-center transition-colors"
                  >
                    <Plus className="w-5 h-5 text-gray-600" />
                  </button>
                </div>

                <div className="flex justify-center gap-3 mb-8">
                  {[10, 25, 50, 100].map((num) => (
                    <button
                      key={num}
                      onClick={() => setCardQuantity(num)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        cardQuantity === num 
                          ? 'bg-[#003594] text-white' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>

                {/* Voucher Section - Above Continue */}
                <div className="mb-4">
                  {appliedVouchers.length > 0 ? (
                    // Success state - show applied vouchers
                    <div className="space-y-2">
                      {appliedVouchers.map((voucher) => (
                        <div 
                          key={voucher.code} 
                          className="flex items-center justify-center gap-2 text-sm"
                        >
                          <span className="text-green-600">✓</span>
                          <span className="text-green-700">
                            {voucher.voucher_type === 'cards' 
                              ? `${voucher.value} free card${voucher.value > 1 ? 's' : ''} - discount applied`
                              : `$${voucher.value} discount applied`
                            }
                          </span>
                          <button 
                            onClick={() => handleRemoveVoucher(voucher.code)}
                            className="text-gray-400 hover:text-red-500 text-xs ml-1"
                            data-testid={`remove-voucher-${voucher.code}`}
                          >
                            (remove)
                          </button>
                        </div>
                      ))}
                      {appliedVouchers.some(v => v.stackable) && !showVoucherInput && (
                        <button
                          onClick={() => setShowVoucherInput(true)}
                          className="text-sm text-gray-500 hover:text-gray-700 underline"
                        >
                          Add another code
                        </button>
                      )}
                      {appliedVouchers.some(v => v.stackable) && showVoucherInput && (
                        <div className="flex items-center justify-center gap-2 mt-2">
                          <Input
                            value={voucherCode}
                            onChange={(e) => {
                              setVoucherCode(e.target.value.toUpperCase());
                              setVoucherError(null);
                            }}
                            placeholder="Enter code"
                            className="font-mono uppercase text-sm h-8 w-36 text-center"
                            data-testid="voucher-input"
                            onKeyPress={(e) => e.key === 'Enter' && handleApplyVoucher()}
                            autoFocus
                          />
                          <Button 
                            onClick={handleApplyVoucher}
                            disabled={isValidatingVoucher || !voucherCode.trim()}
                            variant="ghost"
                            size="sm"
                            className="text-gray-500 hover:text-gray-700"
                            data-testid="apply-voucher-btn"
                          >
                            {isValidatingVoucher ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                          </Button>
                          <button
                            onClick={() => {
                              setShowVoucherInput(false);
                              setVoucherCode('');
                              setVoucherError(null);
                            }}
                            className="text-gray-400 hover:text-gray-600 text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                      {voucherError && (
                        <p className="text-xs text-red-500 text-center">{voucherError}</p>
                      )}
                    </div>
                  ) : showVoucherInput ? (
                    // Expanded input state
                    <div className="flex items-center justify-center gap-2">
                      <Input
                        value={voucherCode}
                        onChange={(e) => {
                          setVoucherCode(e.target.value.toUpperCase());
                          setVoucherError(null);
                        }}
                        placeholder="Enter code"
                        className="font-mono uppercase text-sm h-9 w-36 text-center"
                        data-testid="voucher-input"
                        onKeyPress={(e) => e.key === 'Enter' && handleApplyVoucher()}
                        autoFocus
                      />
                      <Button 
                        onClick={handleApplyVoucher}
                        disabled={isValidatingVoucher || !voucherCode.trim()}
                        variant="ghost"
                        size="sm"
                        className="text-gray-500 hover:text-gray-700"
                        data-testid="apply-voucher-btn"
                      >
                        {isValidatingVoucher ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                      </Button>
                      <button
                        onClick={() => {
                          setShowVoucherInput(false);
                          setVoucherCode('');
                          setVoucherError(null);
                        }}
                        className="text-gray-400 hover:text-gray-600 text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    // Collapsed link state
                    <button
                      onClick={() => setShowVoucherInput(true)}
                      className="text-sm text-gray-500 hover:text-gray-700 underline"
                      data-testid="voucher-link"
                    >
                      Have a coupon code?
                    </button>
                  )}
                  {voucherError && !appliedVouchers.length && (
                    <p className="text-xs text-red-500 text-center mt-1">{voucherError}</p>
                  )}
                </div>

                <Button
                  onClick={handleStep1Complete}
                  disabled={isLoading}
                  className="bg-[#003594] hover:bg-[#002554] text-white px-12 py-6 text-base font-semibold rounded-lg mb-6"
                >
                  {isLoading ? 'Creating...' : 'Continue'}
                </Button>

                {/* Promo Pricing Display */}
                {promoPricing && promoPricing.is_valid && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6 mb-6">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <span className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                        {promoPricing.label || 'SPECIAL OFFER'}
                      </span>
                    </div>
                    
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600 mb-1">
                        ${promoPricing.price_per_card.toFixed(2)}/card
                      </p>
                      <p className="text-lg font-semibold text-green-700 mb-2">
                        {cardQuantity} cards = ${(cardQuantity * promoPricing.price_per_card).toFixed(2)}
                      </p>
                      <p className="text-xs text-green-600">
                        Expires {new Date(promoPricing.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {promoPricing.single_use && ' • First submission only'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Dealer Pricing Display */}
                {user?.dealer_status === 'approved' && dealerPricingInfo && (
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">DEALER PRICING</span>
                      {dealerPricingInfo.has_custom_rate && (
                        <span className="bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">CUSTOM RATE</span>
                      )}
                    </div>
                    
                    {dealerPricingInfo.dealer_eligible ? (
                      <>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="text-center p-3 bg-white/60 rounded-lg">
                            <p className="text-xs text-gray-500 mb-1">Standard Price</p>
                            <p className="text-lg font-bold text-gray-400 line-through">
                              ${dealerPricingInfo.standard_pricing.total.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-400">
                              ${dealerPricingInfo.standard_pricing.price_per_card.toFixed(2)}/card
                            </p>
                          </div>
                          <div className={`text-center p-3 bg-white rounded-lg border-2 ${dealerPricingInfo.has_custom_rate ? 'border-purple-400' : 'border-blue-300'}`}>
                            <p className={`text-xs font-medium mb-1 ${dealerPricingInfo.has_custom_rate ? 'text-purple-600' : 'text-blue-600'}`}>
                              {dealerPricingInfo.has_custom_rate ? 'Your Custom Rate' : 'Your Dealer Price'}
                            </p>
                            <p className={`text-lg font-bold ${dealerPricingInfo.has_custom_rate ? 'text-purple-600' : 'text-blue-600'}`}>
                              ${dealerPricingInfo.dealer_pricing.total.toFixed(2)}
                            </p>
                            <p className={`text-xs ${dealerPricingInfo.has_custom_rate ? 'text-purple-500' : 'text-blue-500'}`}>
                              ${dealerPricingInfo.dealer_pricing.price_per_card.toFixed(2)}/card
                            </p>
                          </div>
                        </div>
                        <div className="text-center">
                          <p className="text-green-600 font-bold text-lg">
                            You Save ${dealerPricingInfo.savings.toFixed(2)} ({dealerPricingInfo.savings_percent}% off!)
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {dealerPricingInfo.has_custom_rate ? (
                              'Negotiated custom rate applies to all quantities'
                            ) : (
                              <>
                                {dealerPricingInfo.dealer_pricing.tier_name}
                                {dealerPricingInfo.dealer_pricing.has_mgp && ' with Minimum Grade Protection'}
                              </>
                            )}
                          </p>
                        </div>
                      </>
                    ) : (
                      <div className="text-center">
                        <p className="text-amber-600 font-medium mb-2">
                          Submit {dealerPricingInfo.dealer_pricing?.min_cards || 250}+ cards to unlock dealer pricing
                        </p>
                        <p className="text-sm text-gray-500">
                          Current quantity ({cardQuantity}) doesn&apos;t qualify for dealer pricing tiers
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-gray-50 rounded-xl p-6 mt-8">
                  <h3 className="font-semibold text-gray-900 mb-4 text-center">Pricing Tiers</h3>
                  <div className="space-y-3">
                    {pricingRules.filter(r => r.is_active).map((rule) => (
                      <div 
                        key={rule.id}
                        className={`flex justify-between items-center p-3 rounded-lg ${
                          pricing.tierName === rule.tier_name 
                            ? 'bg-[#003594]/10 border-2 border-[#003594]' 
                            : 'bg-white'
                        }`}
                      >
                        <div className="flex flex-col">
                          <span className={`font-medium ${pricing.tierName === rule.tier_name ? 'text-[#003594]' : 'text-gray-900'}`}>
                            {rule.tier_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {rule.description || `${rule.min_cards}${rule.max_cards ? `-${rule.max_cards}` : '+'} cards`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {rule.is_launch_special && (
                            <span className="text-xs bg-[#FFC72C] text-[#002554] px-2 py-0.5 rounded-full">Launch Special</span>
                          )}
                          <span className={`font-bold ${pricing.tierName === rule.tier_name ? 'text-[#003594]' : 'text-gray-500'}`}>
                            ${rule.price_per_card}/card
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Card Details */}
          {currentStep === 2 && (
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Card Details</h2>
              <p className="text-gray-500 mb-6">Our experts will research & ID all cards upon receipt.</p>

              <Button
                onClick={() => handleStep2Complete(true)}
                disabled={isLoading}
                className="bg-[#003594] hover:bg-[#002554] text-white px-8 py-3 text-sm font-semibold rounded-lg mb-8"
              >
                {isLoading ? 'Saving...' : 'Skip This Step'}
              </Button>

              <div className="space-y-2 max-h-[450px] overflow-y-auto text-left">
                {cards.map((card, index) => (
                  <div key={card.id}>
                    <button
                      onClick={() => toggleCardExpand(card.id)}
                      className="w-full flex items-center justify-between py-3 hover:bg-gray-50 transition-colors rounded-lg px-2"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold text-gray-600">{index + 1}</span>
                        </div>
                        <span className="font-medium text-gray-900">Card {index + 1}</span>
                      </div>
                      {expandedCards[card.id] ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </button>

                    {expandedCards[card.id] && (
                      <div className="py-4 pl-14">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="space-y-2">
                            <Label className="text-gray-700 text-sm">Year</Label>
                            <Input value={card.year} onChange={(e) => updateCard(card.id, 'year', e.target.value)} placeholder="e.g., 1986" className="border-gray-300" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-700 text-sm">Set / Brand</Label>
                            <Input value={card.set_brand} onChange={(e) => updateCard(card.id, 'set_brand', e.target.value)} placeholder="e.g., Fleer Basketball" className="border-gray-300" />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="space-y-2">
                            <Label className="text-gray-700 text-sm">Card Number</Label>
                            <Input value={card.card_number} onChange={(e) => updateCard(card.id, 'card_number', e.target.value)} placeholder="e.g., #57" className="border-gray-300" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-700 text-sm">Player / Subject Name</Label>
                            <Input value={card.player_name} onChange={(e) => updateCard(card.id, 'player_name', e.target.value)} placeholder="e.g., Michael Jordan" className="border-gray-300" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-700 text-sm">Insert / Parallel</Label>
                          <Input value={card.insert_parallel} onChange={(e) => updateCard(card.id, 'insert_parallel', e.target.value)} placeholder="e.g., Refractor, /99" className="border-gray-300" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 3: Return Address */}
          {currentStep === 3 && (
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Return Shipping</h2>
              <p className="text-gray-500 mb-8">Where should we send your graded cards?</p>

              {isLoadingAddresses ? (
                <div className="py-12">
                  <div className="w-12 h-12 border-4 border-[#003594] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading addresses...</p>
                </div>
              ) : (
                <div className="max-w-lg mx-auto space-y-4 text-left">
                  {/* Saved Addresses */}
                  {savedAddresses.map((addr) => (
                    <button
                      key={addr.id}
                      onClick={() => { setSelectedAddressId(addr.id); setShowNewAddressForm(false); }}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        selectedAddressId === addr.id && !showNewAddressForm
                          ? 'border-[#003594] bg-[#003594]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-5 h-5 text-gray-500" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900">{addr.label}</span>
                            {addr.is_default && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Default</span>}
                          </div>
                          <p className="text-gray-600 text-sm">{addr.name}</p>
                          <p className="text-gray-600 text-sm">{addr.street_address}</p>
                          <p className="text-gray-600 text-sm">{addr.city}, {addr.state} {addr.zip_code}</p>
                        </div>
                        {selectedAddressId === addr.id && !showNewAddressForm && (
                          <div className="w-6 h-6 bg-[#003594] rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  ))}

                  {/* Add New Address */}
                  {!showNewAddressForm ? (
                    <button
                      onClick={() => { setShowNewAddressForm(true); setSelectedAddressId(null); }}
                      className="w-full p-4 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#003594] hover:bg-gray-50 transition-all flex items-center justify-center gap-2 text-gray-600 hover:text-[#003594]"
                    >
                      <Plus className="w-5 h-5" />
                      <span className="font-medium">Add New Address</span>
                    </button>
                  ) : (
                    <div className="p-6 rounded-xl border-2 border-[#003594] bg-[#003594]/5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900">New Address</h3>
                        {savedAddresses.length > 0 && (
                          <button onClick={() => { setShowNewAddressForm(false); setSelectedAddressId(savedAddresses[0]?.id); }} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
                        )}
                      </div>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-gray-700 text-sm">Label (optional)</Label>
                            <Input name="label" value={newAddress.label} onChange={handleNewAddressChange} placeholder="e.g., Home, Office" className="border-gray-300" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-700 text-sm">Full Name *</Label>
                            <Input name="name" value={newAddress.name} onChange={handleNewAddressChange} placeholder="John Doe" className="border-gray-300" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-700 text-sm">Street Address *</Label>
                          <Input name="street_address" value={newAddress.street_address} onChange={handleNewAddressChange} placeholder="123 Main St, Apt 4" className="border-gray-300" />
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label className="text-gray-700 text-sm">City *</Label>
                            <Input name="city" value={newAddress.city} onChange={handleNewAddressChange} placeholder="New York" className="border-gray-300" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-700 text-sm">State *</Label>
                            <Input name="state" value={newAddress.state} onChange={handleNewAddressChange} placeholder="NY" className="border-gray-300" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-700 text-sm">ZIP *</Label>
                            <Input name="zip_code" value={newAddress.zip_code} onChange={handleNewAddressChange} placeholder="10001" className="border-gray-300" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-700 text-sm">Phone (optional)</Label>
                          <Input name="phone" value={newAddress.phone} onChange={handleNewAddressChange} placeholder="(555) 123-4567" className="border-gray-300" />
                        </div>
                        <div className="space-y-3 pt-2">
                          <div className="flex items-center gap-2">
                            <Checkbox id="saveAddress" checked={saveAddress} onCheckedChange={setSaveAddress} />
                            <label htmlFor="saveAddress" className="text-sm text-gray-700">Save this address for future use</label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox id="defaultAddress" checked={newAddress.is_default} onCheckedChange={(checked) => setNewAddress({...newAddress, is_default: checked})} />
                            <label htmlFor="defaultAddress" className="text-sm text-gray-700">Make this my default address</label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Estimated Value - Required */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <Label className="text-gray-700 font-medium">
                      Estimated Total Value of Submission <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-xs text-gray-500 mb-3">
                      Required for return shipping insurance purposes
                    </p>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={estimatedValueDisplay}
                        onChange={handleEstimatedValueChange}
                        onBlur={handleEstimatedValueBlur}
                        onFocus={handleEstimatedValueFocus}
                        placeholder="2,500"
                        className={`pl-7 border-gray-300 ${!estimatedValue ? 'border-amber-300' : ''}`}
                        data-testid="estimated-value-input"
                      />
                    </div>
                    
                    {/* Validation messages */}
                    {!estimatedValue && (
                      <p className="text-xs text-amber-600 mt-2">
                        Please enter the estimated total value of all cards in this submission
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 4: Review Order */}
          {currentStep === 4 && reviewData && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Review Your Order</h2>
              <p className="text-gray-500 mb-8 text-center">Please confirm all details before proceeding to payment.</p>

              <div className="max-w-lg mx-auto space-y-6">
                {/* Submission Info */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-gray-900">Submission Details</h3>
                    <span className="text-sm font-semibold text-[#003594]">{reviewData.submission_number}</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Number of Cards</span>
                      <span className="font-medium text-gray-900">{reviewData.card_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Price per Card</span>
                      <span className="font-medium text-gray-900">${reviewData.price_per_card?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pricing Tier</span>
                      <span className="font-medium text-gray-900">
                        {reviewData.dealer_tier_applied === 'custom' ? 'Custom Rate' : reviewData.pricing_tier}
                      </span>
                    </div>
                    
                    {/* Dealer Savings Display */}
                    {reviewData.is_dealer_submission && reviewData.dealer_savings > 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded">DEALER</span>
                            {reviewData.dealer_tier_applied === 'custom' && (
                              <span className="bg-purple-600 text-white text-xs font-bold px-2 py-0.5 rounded">CUSTOM</span>
                            )}
                            <span className="text-sm text-green-700">Savings Applied</span>
                          </div>
                          <span className="font-bold text-green-600">-${reviewData.dealer_savings?.toFixed(2)}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Standard price would be ${reviewData.standard_price_would_be?.toFixed(2)}
                        </p>
                        {reviewData.dealer_tier_applied === 'custom' ? (
                          <p className="text-xs text-purple-600 mt-1 font-medium">
                            Negotiated custom rate applied
                          </p>
                        ) : reviewData.mgp_enabled && (
                          <p className="text-xs text-purple-600 mt-1 font-medium">
                            Minimum Grade Protection included
                          </p>
                        )}
                      </div>
                    )}
                    
                    {reviewData.estimated_submission_value > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Declared Value (for insurance)</span>
                        <span className="font-medium text-gray-900">${Number(reviewData.estimated_submission_value).toLocaleString()}</span>
                      </div>
                    )}
                    
                    {/* Cost Breakdown */}
                    <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Grading ({reviewData.card_count} × ${reviewData.price_per_card?.toFixed(2)})</span>
                        <span className="font-medium text-gray-900">${(reviewData.grading_cost || reviewData.card_count * reviewData.price_per_card)?.toFixed(2)}</span>
                      </div>
                      
                      {/* Shipping with weight info */}
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="text-gray-600">Return Shipping</span>
                          {reviewData.package_weight_lbs && (
                            <span className="text-xs text-gray-400 ml-1">({reviewData.package_weight_lbs} lbs)</span>
                          )}
                        </div>
                        <span className="font-medium text-gray-900">${(reviewData.shipping_cost ?? 7)?.toFixed(2)}</span>
                      </div>
                      
                      {/* Insurance */}
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="text-gray-600">Insurance</span>
                          {reviewData.insurance_cost === 0 && (
                            <span className="text-xs text-green-600 ml-1">(included)</span>
                          )}
                        </div>
                        <span className="font-medium text-gray-900">
                          {reviewData.insurance_cost > 0 ? `$${reviewData.insurance_cost?.toFixed(2)}` : 'Included'}
                        </span>
                      </div>
                      
                      {/* Discount Applied */}
                      {reviewData.voucher_discount > 0 && (
                        <div className="flex justify-between items-center text-green-600">
                          <span className="flex items-center gap-1">
                            <span>Discount Applied</span>
                            {reviewData.voucher_codes_used && reviewData.voucher_codes_used.length > 0 && (
                              <span className="text-xs text-gray-400 font-mono">
                                ({reviewData.voucher_codes_used[0]})
                              </span>
                            )}
                          </span>
                          <span className="font-medium">-${reviewData.voucher_discount?.toFixed(2)}</span>
                        </div>
                      )}
                      
                      {/* Total */}
                      <div className="flex justify-between text-lg pt-2 border-t border-gray-200 mt-2">
                        <span className="font-bold text-gray-900">Total</span>
                        <span className="font-bold text-[#003594]">${reviewData.total_price?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                {reviewData.shipping_address && (
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Return Shipping Address</h3>
                    <div className="text-sm text-gray-600">
                      <p className="font-medium text-gray-900">{reviewData.shipping_address.name}</p>
                      <p>{reviewData.shipping_address.street_address}</p>
                      <p>{reviewData.shipping_address.city}, {reviewData.shipping_address.state} {reviewData.shipping_address.zip_code}</p>
                      {reviewData.shipping_address.phone && <p>{reviewData.shipping_address.phone}</p>}
                    </div>
                  </div>
                )}

                {/* Card Details */}
                {reviewData.cards && reviewData.cards.length > 0 && (
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Card Details ({reviewData.cards.length} entered)</h3>
                    <div className="space-y-2 text-sm max-h-40 overflow-y-auto">
                      {reviewData.cards.map((card, i) => (
                        <div key={card.id} className="flex items-center gap-2 text-gray-600">
                          <span className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium">{i + 1}</span>
                          <span>{[card.year, card.set_brand, card.player_name, card.card_number].filter(Boolean).join(' - ') || 'Details not entered'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Payment Method Selection */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Payment Method</h3>
                  
                  {/* Payment Disclaimer - More Prominent */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-800 font-medium flex items-center gap-2">
                      <CreditCard className="w-4 h-4 flex-shrink-0" />
                      Your card will not be charged now. Payment is processed once we receive your cards.
                    </p>
                  </div>
                  
                  {savedPaymentMethod?.has_saved_method ? (
                    <div className="space-y-3">
                      {/* Use saved card option */}
                      <button
                        type="button"
                        onClick={() => setPaymentChoice('saved')}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          paymentChoice === 'saved'
                            ? 'border-[#003594] bg-[#003594]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-gray-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">Use saved card</p>
                            <p className="text-sm text-gray-600">
                              {savedPaymentMethod.payment_method_brand} ending in {savedPaymentMethod.payment_method_last4}
                            </p>
                          </div>
                          {paymentChoice === 'saved' && (
                            <div className="w-6 h-6 bg-[#003594] rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </button>

                      {/* Use different card option */}
                      <button
                        type="button"
                        onClick={() => setPaymentChoice('new')}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          paymentChoice === 'new'
                            ? 'border-[#003594] bg-[#003594]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <Plus className="w-5 h-5 text-gray-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">Use a different card</p>
                            <p className="text-sm text-gray-600">Update your default payment method</p>
                          </div>
                          {paymentChoice === 'new' && (
                            <div className="w-6 h-6 bg-[#003594] rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </button>
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl border-2 border-gray-200 bg-white">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#003594]/10 rounded-full flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-[#003594]" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Add Payment Method</p>
                          <p className="text-sm text-gray-600">You&apos;ll be redirected to a secure form to enter details</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: Confirmation */}
          {currentStep === 5 && (
            <div className="py-8">
              {/* Success Header */}
              <div className="text-center mb-8">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-12 h-12 text-green-500" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Submission Complete!</h2>
                <p className="text-gray-500 mb-4">Your order has been successfully placed.</p>
                
                {/* Submission Number - Large and Bold */}
                <div className="bg-[#003594]/5 border-2 border-[#003594]/20 rounded-xl p-4 max-w-md mx-auto">
                  <p className="text-sm text-gray-500 mb-1">Submission Number</p>
                  <p className="text-2xl font-bold text-[#003594]">{submission?.submission_number}</p>
                  <p className="text-sm text-gray-600 mt-2">
                    {submission?.card_count} card{submission?.card_count !== 1 ? 's' : ''} @ ${submission?.price_per_card?.toFixed(2)}/card = <span className="font-semibold">${submission?.total_price?.toFixed(2)}</span>
                  </p>
                </div>
              </div>

              {/* PROMINENT DOWNLOAD SECTION */}
              <div className="max-w-2xl mx-auto mb-8">
                <div className="bg-gradient-to-r from-[#003594] to-[#002554] rounded-2xl p-6 text-white shadow-lg">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-center sm:text-left">
                      <h3 className="text-xl font-bold mb-1 flex items-center justify-center sm:justify-start gap-2">
                        <FileText className="w-6 h-6" />
                        Download Your Packing Slip
                      </h3>
                      <p className="text-blue-100 text-sm">Print and include with your cards for faster processing</p>
                    </div>
                    <Button 
                      className="bg-white text-[#003594] hover:bg-gray-100 px-8 py-6 text-lg font-bold flex items-center gap-3 shadow-md min-w-[220px] justify-center"
                      onClick={handleDownloadPackingSlip}
                      disabled={isGeneratingPDF}
                      data-testid="download-packing-slip-btn"
                    >
                      {isGeneratingPDF ? (
                        <>
                          <Loader2 className="w-6 h-6 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Download className="w-6 h-6" />
                          Download PDF
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* PDF Error Message */}
                  {pdfError && (
                    <div className="mt-4 bg-red-500/20 border border-red-300/30 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-white mb-2">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span className="text-sm">{pdfError}</span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                        onClick={handlePrintableVersion}
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Open Printable Version
                      </Button>
                    </div>
                  )}
                  
                  {/* Alternative: View printable version link */}
                  <div className="mt-4 text-center">
                    <Link 
                      to={`/submissions/${submissionId}/print`}
                      target="_blank"
                      className="text-blue-100 hover:text-white text-sm underline"
                    >
                      Or view printable version in browser
                    </Link>
                  </div>
                </div>
              </div>

              <div className="max-w-2xl mx-auto space-y-6">
                {/* Next Steps Section */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#003594]" />
                    Next Steps
                  </h3>
                  <ol className="space-y-4">
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">1</span>
                      <div>
                        <p className="font-medium text-gray-900">Print your packing slip (downloaded above)</p>
                        <p className="text-sm text-gray-500">The PDF includes all submission details and shipping instructions</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">2</span>
                      <div>
                        <p className="font-medium text-gray-900">Include the packing slip with your cards</p>
                        <p className="text-sm text-gray-500">Place it on top of your cards inside the package</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">3</span>
                      <div>
                        <p className="font-medium text-gray-900">Ship your cards to our grading lab</p>
                        <p className="text-sm text-gray-500">See shipping address below</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">4</span>
                      <div>
                        <p className="font-medium text-gray-900">We&apos;ll email you when your cards arrive</p>
                        <p className="text-sm text-gray-500">Check {user?.email} for updates</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">5</span>
                      <div>
                        <p className="font-medium text-gray-900">Payment will be processed when we receive your cards</p>
                        <p className="text-sm text-gray-500">Your card on file will be charged ${submission?.total_price?.toFixed(2)}</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <span className="w-8 h-8 bg-[#003594] text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">6</span>
                      <div>
                        <p className="font-medium text-gray-900">Track your submission status in your dashboard</p>
                        <p className="text-sm text-gray-500">View real-time updates on your submission progress</p>
                      </div>
                    </li>
                  </ol>
                </div>

                {/* Shipping Addresses */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-[#003594]" />
                    Ship Your Cards To
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">Choose the address that matches your shipping carrier:</p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* USPS Address */}
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                          <Mail className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-blue-800">USPS</span>
                      </div>
                      <div className="text-gray-700 text-sm">
                        <p className="font-semibold">NICE Grading</p>
                        <p>Attn: Receiving</p>
                        <p>P.O. Box 30041</p>
                        <p>Rochester, NY 14603</p>
                      </div>
                    </div>
                    
                    {/* UPS/FedEx Address */}
                    <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
                          <Truck className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-bold text-amber-800">UPS / FedEx</span>
                      </div>
                      <div className="text-gray-700 text-sm">
                        <p className="font-semibold">NICE Grading</p>
                        <p>Attn: Receiving</p>
                        <p>250 Cumberland St.</p>
                        <p>Ste. 202</p>
                        <p>Rochester, NY 14605</p>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-[#003594] font-medium mt-4 text-center">
                    Reference: Submission #{submission?.submission_number}
                  </p>
                </div>

                {/* Important Reminders */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <h3 className="font-semibold text-amber-800 mb-4 flex items-center gap-2">
                    <Info className="w-5 h-5" />
                    Important Reminders
                  </h3>
                  <ul className="space-y-2 text-amber-800">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span>Package your cards securely to prevent damage during shipping</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span>Include your packing slip inside the package</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span>We recommend insuring your shipment for valuable cards</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span>Keep your tracking number for your records</span>
                    </li>
                  </ul>
                </div>

                {/* Shipping Instructions Link */}
                <div className="bg-gradient-to-r from-[#003594] to-blue-600 rounded-lg p-5 text-white">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-1">Need packing help?</h3>
                      <p className="text-blue-100 text-sm mb-3">
                        View our detailed shipping instructions with step-by-step packing guide, best practices, and tips for protecting your cards in transit.
                      </p>
                      <Link 
                        to="/shipping-instructions" 
                        className="inline-flex items-center gap-2 bg-white text-[#003594] px-4 py-2 rounded-lg font-medium hover:bg-blue-50 transition-colors text-sm"
                        data-testid="shipping-instructions-link"
                      >
                        View Shipping Instructions
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                  <Link to="/dashboard">
                    <Button variant="outline" className="w-full sm:w-auto px-8 py-6 flex items-center justify-center gap-2 border-2">
                      <ExternalLink className="w-5 h-5" />
                      View Dashboard
                    </Button>
                  </Link>
                </div>

                {/* Start New Submission Link */}
                <div className="text-center pt-2">
                  <Link to="/new-submission" onClick={() => window.location.reload()} className="text-[#003594] hover:underline text-sm font-medium">
                    Start a New Submission
                  </Link>
                </div>

                {/* Email Confirmation Notice */}
                <div className="text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>A confirmation email has been sent to {user?.email}</span>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          {currentStep > 1 && currentStep < 5 && (
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <Button variant="outline" onClick={prevStep} className="flex items-center gap-2">
                <ChevronLeft className="w-4 h-4" /> Back
              </Button>
              
              {currentStep === 2 && (
                <Button onClick={() => handleStep2Complete(false)} disabled={isLoading} className="bg-[#003594] hover:bg-[#002554] text-white px-8 flex items-center gap-2">
                  {isLoading ? 'Saving...' : 'Continue'} <ChevronRight className="w-4 h-4" />
                </Button>
              )}
              
              {currentStep === 3 && (
                <Button onClick={handleStep3Complete} disabled={isLoading} className="bg-[#003594] hover:bg-[#002554] text-white px-8 flex items-center gap-2">
                  {isLoading ? 'Saving...' : 'Continue'} <ChevronRight className="w-4 h-4" />
                </Button>
              )}
              
              {currentStep === 4 && (
                <Button onClick={handlePayment} disabled={isLoading} className="bg-[#003594] hover:bg-[#002554] text-white px-8 flex items-center gap-2">
                  {isLoading ? 'Processing...' : (
                    paymentChoice === 'saved' && savedPaymentMethod?.has_saved_method 
                      ? 'Complete Submission' 
                      : savedPaymentMethod?.has_saved_method
                        ? 'Update Payment Method'
                        : 'Add Payment Info'
                  )} <ChevronRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewSubmissionPage;
